import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router';

import {
  buildGuideBootstrapStorageKey,
  markGuideBootstrapActive,
  hasGuideBootstrapAttempted,
  markGuideBootstrapAttempted,
  markGuideHistoryFound,
  markGuidePulseNeeded,
  readGuideBootstrapState,
  useGuideStorageUserId,
} from '@/components/folio/folio-guide-bootstrap';
import { ErrorRecoveryBoundary } from '@/components/common/error-recovery-boundary';
import { readFolioTargetHighlight } from '@/components/folio/folio-navigation-state';
import { FolioSessionProvider } from '@/components/folio/folio-session-provider';
import { FolioToggleProvider } from '@/components/folio/folio-toggle-provider';
import { setFolioPanelElement } from '@/components/folio/use-folio-panel-width';
import { AgentConnectionProvider } from '@/components/agent-connection/agent-connection-provider';
import { ReleaseNewsDialog } from '@/components/release-news/release-news-dialog';
import { releaseNewsClaimsPageLoad } from '@/components/release-news/release-news-model';
import { ReleaseNotesOpenerCtx } from '@/components/release-news/release-notes-opener';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { PageOutlet } from '@/components/layout/page-outlet';
import { AppBreadcrumbProvider } from '@/hooks/app-breadcrumb-context';
import { TooltipProvider } from '@/components/ui/tooltip';
import { CommandPalette } from '@/components/command-palette/command-palette';
import { CommandPaletteRegistryProvider } from '@/components/command-palette/command-palette-registry-provider';
import { useFolioSessionCommands, useFolioSessionState } from '@/hooks/use-folio-session';
import { useFolioToggle } from '@/hooks/use-folio-toggle';
import { useIsDesktop, useIsMobile } from '@/hooks/use-mobile';
import { useReleaseNews } from '@/hooks/use-release-news';
import { PageChromeCtx } from '@/hooks/page-chrome-store';
import { apiClient } from '@/lib/api';
import { isWorkspaceLocation } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { withCleanup } from '@/lib/with-cleanup';
import { AppLayoutHeader } from './app-layout-header';
import { AppSidebar } from './app-sidebar';
import { useFoldingSidebar } from './use-folding-sidebar';

const LazyFolioConversation = lazy(async () => ({
  default: (await import('@/components/folio/folio-conversation')).FolioConversation,
}));

const FOLIO_PANEL_DEFAULT_WIDTH = 380;
const FOLIO_PANEL_MIN_WIDTH = 320;
const FOLIO_PANEL_MAX_WIDTH = 900;
const FOLIO_PANEL_WIDTH_STORAGE_KEY = 'contract-analyzer:folio-panel-width';
const MAIN_CONTENT_SCROLL_STORAGE_KEY_PREFIX = 'contract-analyzer:main-content-scroll:';
const MAIN_CONTENT_SCROLL_RESTORE_MAX_ATTEMPTS = 60;
const MAIN_CONTENT_SCROLL_RESTORE_RETRY_MS = 16;
const MAIN_CONTENT_SCROLL_PERSIST_THROTTLE_MS = 100;
const FOLIO_TARGET_HIGHLIGHT_RETRY_MS = 50;
const FOLIO_TARGET_HIGHLIGHT_MAX_ATTEMPTS = 40;
const FOLIO_TARGET_HIGHLIGHT_DURATION_MS = 1800;

function findFolioTarget(targetTestId: string): HTMLElement | null {
  for (const candidate of document.querySelectorAll('[data-folio-target]')) {
    if (
      candidate instanceof HTMLElement &&
      candidate.dataset['folioTarget'] === targetTestId
    ) {
      return candidate;
    }
  }
  return null;
}

function useFolioTargetHighlight() {
  const location = useLocation();

  useEffect(() => {
    const highlight = readFolioTargetHighlight(location.state);
    if (highlight === null) {
      return;
    }

    let retryId: number | null = null;
    let clearHighlightId: number | null = null;
    let highlightedElement: HTMLElement | null = null;
    let previousTabIndex: string | null = null;
    let attempts = 0;

    const clearHighlight = () => {
      if (highlightedElement === null) {
        return;
      }
      highlightedElement.classList.remove('ca-folio-target-highlight');
      if (previousTabIndex === null) {
        highlightedElement.removeAttribute('tabindex');
      } else {
        highlightedElement.setAttribute('tabindex', previousTabIndex);
      }
      highlightedElement = null;
    };

    const tryHighlight = () => {
      attempts += 1;
      const target = findFolioTarget(highlight.target_test_id);
      if (target === null) {
        if (attempts < FOLIO_TARGET_HIGHLIGHT_MAX_ATTEMPTS) {
          retryId = window.setTimeout(tryHighlight, FOLIO_TARGET_HIGHLIGHT_RETRY_MS);
        }
        return;
      }

      retryId = null;
      highlightedElement = target;
      previousTabIndex = target.getAttribute('tabindex');
      if (previousTabIndex === null) {
        target.setAttribute('tabindex', '-1');
      }
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ block: 'center', inline: 'nearest' });
      }
      target.focus({ preventScroll: true });
      // scrollIntoView and focus above flush styles, so a re-highlight within
      // the duration sees the cleanup's removal and restarts the animation.
      target.classList.add('ca-folio-target-highlight');
      clearHighlightId = window.setTimeout(
        clearHighlight,
        FOLIO_TARGET_HIGHLIGHT_DURATION_MS,
      );
    };

    tryHighlight();

    return () => {
      if (retryId !== null) {
        window.clearTimeout(retryId);
      }
      if (clearHighlightId !== null) {
        window.clearTimeout(clearHighlightId);
      }
      clearHighlight();
    };
  }, [location.key, location.state]);
}

function clampFolioWidth(value: number): number {
  if (!Number.isFinite(value)) {
    return FOLIO_PANEL_DEFAULT_WIDTH;
  }
  const viewportCap = typeof window === 'undefined' ? FOLIO_PANEL_MAX_WIDTH : Math.max(FOLIO_PANEL_MIN_WIDTH, window.innerWidth - 320);
  const max = Math.min(FOLIO_PANEL_MAX_WIDTH, viewportCap);
  return Math.min(max, Math.max(FOLIO_PANEL_MIN_WIDTH, Math.round(value)));
}

function readStoredFolioWidth(): number {
  if (typeof window === 'undefined') {
    return FOLIO_PANEL_DEFAULT_WIDTH;
  }
  const raw = window.localStorage.getItem(FOLIO_PANEL_WIDTH_STORAGE_KEY);
  if (raw === null) {
    return FOLIO_PANEL_DEFAULT_WIDTH;
  }
  const parsed = Number.parseInt(raw, 10);
  return clampFolioWidth(parsed);
}

function buildMainContentScrollStorageKey(pathname: string, search: string): string {
  return `${MAIN_CONTENT_SCROLL_STORAGE_KEY_PREFIX}${pathname}${search}`;
}

function readStoredMainContentScroll(pathname: string, search: string): number {
  if (typeof window === 'undefined') {
    return 0;
  }

  try {
    const raw = window.sessionStorage.getItem(
      buildMainContentScrollStorageKey(pathname, search),
    );
    if (raw === null) {
      return 0;
    }

    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function persistMainContentScroll(pathname: string, search: string, scrollTop: number): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(
      buildMainContentScrollStorageKey(pathname, search),
      String(Math.max(0, Math.round(scrollTop))),
    );
  } catch {
    // Ignore storage failures; scroll persistence is an enhancement.
  }
}

function FolioPanelSlot() {
  const { isOpen, close } = useFolioToggle();
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();
  const [hasOpened, setHasOpened] = useState(isOpen);
  // Counts openings so reopening Sean retries a conversation that failed to
  // load, while closing it leaves the failure alone rather than retrying.
  const [openCount, setOpenCount] = useState(0);
  const [panelWidth, setPanelWidth] = useState<number>(readStoredFolioWidth);
  const [isResizing, setIsResizing] = useState(false);

  // Publish the panel element to the module store so `useFolioPanelWidth`
  // can subscribe without observing the document tree or doing DOM lookups.
  const asideRef = useCallback((node: HTMLElement | null) => {
    setFolioPanelElement(node);
    return () => {
      setFolioPanelElement(null);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      setHasOpened(true);
      setOpenCount((count) => count + 1);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const handleMove = (event: PointerEvent) => {
      event.preventDefault();
      const next = clampFolioWidth(window.innerWidth - event.clientX);
      setPanelWidth(next);
    };

    const handleUp = () => {
      setIsResizing(false);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isResizing]);

  useEffect(() => {
    if (isResizing || typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(FOLIO_PANEL_WIDTH_STORAGE_KEY, String(panelWidth));
  }, [panelWidth, isResizing]);

  const startResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDesktop || !isOpen) {
      return;
    }
    event.preventDefault();
    setIsResizing(true);
  };

  const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isDesktop || !isOpen) {
      return;
    }
    const step = event.shiftKey ? 40 : 16;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setPanelWidth((current) => clampFolioWidth(current + step));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setPanelWidth((current) => clampFolioWidth(current - step));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setPanelWidth(FOLIO_PANEL_DEFAULT_WIDTH);
    }
  };

  // On mobile/tablet overlay, don't mount until first open
  if (!isDesktop && !hasOpened) {
    return null;
  }

  const desktopWidthStyle = isDesktop && isOpen ? { width: `${String(panelWidth)}px` } : undefined;
  const contentWidthStyle = isDesktop ? { width: `${String(panelWidth)}px`, minWidth: `${String(panelWidth)}px` } : undefined;

  return (
    <aside
      ref={asideRef}
      id="folio-panel"
      role="complementary"
      aria-label="Sean assistant"
      inert={!isOpen}
      aria-hidden={!isOpen}
      style={desktopWidthStyle}
      className={cn(
        'bg-background',
        isDesktop
          ? cn(
              // Docks in one frame. Ctrl/Cmd+Shift+F toggles it many times a
              // day, and an animated width reflowed the header, the grid and
              // every width observer under it on each frame.
              'sticky top-0 h-svh shrink-0 overflow-hidden border-l',
              !isOpen && 'w-0 border-l-transparent',
            )
          : cn(
              'fixed inset-y-0 right-0 border-l shadow-lg',
              isMobile ? 'z-30 w-[min(380px,100vw)]' : 'z-20 w-[380px]',
              'transition-transform duration-200 ease-out-quart motion-reduce:transition-none',
              isOpen ? 'translate-x-0' : 'translate-x-full',
              !isOpen && 'pointer-events-none',
            ),
      )}
    >
      {isDesktop && isOpen ? (
        <div
          role="separator"
          aria-label="Resize Sean panel"
          aria-orientation="vertical"
          aria-valuemin={FOLIO_PANEL_MIN_WIDTH}
          aria-valuemax={FOLIO_PANEL_MAX_WIDTH}
          aria-valuenow={panelWidth}
          tabIndex={0}
          onPointerDown={startResize}
          onKeyDown={handleResizeKeyDown}
          className={cn(
            'absolute inset-y-0 left-0 z-10 w-1.5 -translate-x-1/2 cursor-col-resize',
            'hover:bg-primary/30 focus-visible:bg-primary/40 focus-visible:outline-none',
            isResizing && 'bg-primary/40',
          )}
        />
      ) : null}
      {hasOpened ? (
        <div className={cn(isDesktop && 'h-full')} style={contentWidthStyle}>
          {/* The conversation carries Sean's Close and Escape; when its chunk
              fails the recovery state brings them, since on a phone the
              header and sidebar are inert behind the open panel. */}
          <ErrorRecoveryBoundary
            resetKey={String(openCount)}
            surface="panel"
            dismiss={isOpen ? { label: 'Close Sean', onDismiss: close } : undefined}
          >
            <Suspense fallback={null}>
              <LazyFolioConversation onClose={close} isOpen={isOpen} />
            </Suspense>
          </ErrorRecoveryBoundary>
        </div>
      ) : null}
    </aside>
  );
}


export function FolioGuideBootstrapCoordinator() {
  const userId = useGuideStorageUserId();
  const { conversationId, pageContext } = useFolioSessionCommands();
  const { currentMode, isStreaming, messages } = useFolioSessionState();
  const { isOpen, open } = useFolioToggle();
  const bootstrapScope = buildGuideBootstrapStorageKey(pageContext, userId);
  const hasGuideMessages = messages.some((message) =>
    message.events.some((event) => event.type === 'guide_step'),
  );
  // Guards against StrictMode double-effect racing two eligibility lookups.
  const dashboardBootstrapInFlightRef = useRef(false);

  useEffect(() => {
    const isProjectScope = pageContext.entities.project_id !== undefined;
    const isTenantDashboard = pageContext.page_id === 'tenant-dashboard';
    if (!isTenantDashboard && !isProjectScope) {
      return;
    }

    const guideState = readGuideBootstrapState(pageContext, userId);

    if (guideState.status === 'bootstrapping' && conversationId !== null) {
      markGuideBootstrapActive(pageContext, userId, conversationId);
      return;
    }

    if (
      isTenantDashboard &&
      !hasGuideBootstrapAttempted(pageContext, userId) &&
      conversationId === null &&
      messages.length === 0 &&
      !isStreaming &&
      !dashboardBootstrapInFlightRef.current
    ) {
      dashboardBootstrapInFlightRef.current = true;
      const controller = new AbortController();
      void (async () => {
        await withCleanup(async () => {
          try {
            const conversations = await apiClient.listFolioConversations();
            if (controller.signal.aborted || hasGuideBootstrapAttempted(pageContext, userId)) {
              return;
            }
            if (conversations.length > 0) {
              markGuideHistoryFound(pageContext, userId);
              return;
            }
            markGuideBootstrapAttempted(pageContext, userId);
            open('bootstrap');
          } catch {
            // Unknown history is not evidence of a first visit.
          }
        }, () => {
          if (!controller.signal.aborted) {
            dashboardBootstrapInFlightRef.current = false;
          }
        });
      })();
      return () => {
        controller.abort();
        dashboardBootstrapInFlightRef.current = false;
      };
    }

    if (
      isProjectScope &&
      guideState.status === 'not_started' &&
      conversationId !== null &&
      (currentMode === 'guide' || hasGuideMessages)
    ) {
      markGuideBootstrapActive(pageContext, userId, conversationId);
      return;
    }

    if (
      guideState.status === 'deferred' &&
      !isOpen &&
      conversationId === null &&
      messages.length === 0 &&
      !isStreaming
    ) {
      markGuidePulseNeeded(pageContext, userId);
    }
    return undefined;
  }, [
    bootstrapScope,
    conversationId,
    currentMode,
    hasGuideMessages,
    isOpen,
    isStreaming,
    messages.length,
    open,
    pageContext,
    userId,
  ]);

  return null;
}

export function AppLayoutShell() {
  const { isOpen } = useFolioToggle();
  const isMobile = useIsMobile();
  const location = useLocation();
  const isOverlayInert = isMobile && isOpen;
  const releaseNews = useReleaseNews();
  const [breadcrumbTail, setBreadcrumbTail] = useState<HTMLElement | null>(null);
  const [breadcrumbTailInUse, setBreadcrumbTailInUse] = useState(false);
  const [pageActions, setPageActions] = useState<HTMLElement | null>(null);
  const pageChrome = useMemo(
    () => ({ breadcrumbTail, breadcrumbTailInUse, setBreadcrumbTailInUse, pageActions }),
    [breadcrumbTail, breadcrumbTailInUse, pageActions],
  );
  // An open result, or the template editor, is a workspace: it fills the main
  // region edge to edge and owns its scrolling (AppLayout folds the sidebar
  // for it too). The URL decides, not the page once its data has loaded, so
  // the first frame already has the workspace's edges and nothing moves when
  // the data arrives (#921).
  const fullBleed = isWorkspaceLocation(location);
  useFolioTargetHighlight();

  useEffect(() => {
    const main = document.getElementById('main-content');
    if (!(main instanceof HTMLElement)) {
      return;
    }

    const targetScrollTop = readStoredMainContentScroll(location.pathname, location.search);
    let restoreTimeoutId: number | null = null;
    let persistTimeoutId: number | null = null;
    let restoreAttempts = 0;
    let isRestoring = targetScrollTop > 0;

    const restore = () => {
      restoreAttempts += 1;
      main.scrollTop = targetScrollTop;

      if (
        targetScrollTop <= 0 ||
        Math.abs(main.scrollTop - targetScrollTop) <= 1 ||
        restoreAttempts >= MAIN_CONTENT_SCROLL_RESTORE_MAX_ATTEMPTS
      ) {
        isRestoring = false;
        restoreTimeoutId = null;
        return;
      }

      restoreTimeoutId = window.setTimeout(restore, MAIN_CONTENT_SCROLL_RESTORE_RETRY_MS);
    };

    restoreTimeoutId = window.setTimeout(restore, 0);

    const persist = () => {
      if (isRestoring && main.scrollTop + 1 < targetScrollTop) {
        return;
      }
      persistMainContentScroll(location.pathname, location.search, main.scrollTop);
    };

    const flushPersist = () => {
      if (persistTimeoutId !== null) {
        window.clearTimeout(persistTimeoutId);
        persistTimeoutId = null;
      }

      persist();
    };

    const schedulePersist = () => {
      if (persistTimeoutId !== null) {
        return;
      }

      persistTimeoutId = window.setTimeout(() => {
        persistTimeoutId = null;
        persist();
      }, MAIN_CONTENT_SCROLL_PERSIST_THROTTLE_MS);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushPersist();
      }
    };

    main.addEventListener('scroll', schedulePersist, { passive: true });
    window.addEventListener('pagehide', flushPersist);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (restoreTimeoutId !== null) {
        window.clearTimeout(restoreTimeoutId);
      }
      flushPersist();
      main.removeEventListener('scroll', schedulePersist);
      window.removeEventListener('pagehide', flushPersist);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.pathname, location.search]);

  return (
    <ReleaseNotesOpenerCtx value={releaseNews.data?.available === true ? releaseNews.showAll : null}>
      <AgentConnectionProvider
        releaseNewsPending={releaseNewsClaimsPageLoad(releaseNews.data, releaseNews.settled, releaseNews.view)}
      >
        <FolioGuideBootstrapCoordinator />
        <CommandPalette />
        <ReleaseNewsDialog releaseNews={releaseNews} />
        <div id="folio-app-background" className="flex h-svh w-full overflow-hidden">
          <div id="folio-app-sidebar" inert={isOverlayInert} aria-hidden={isOverlayInert}>
            <AppSidebar />
          </div>
          <SidebarInset id="folio-app-main" className="min-h-0 min-w-0" inert={isOverlayInert} aria-hidden={isOverlayInert}>
            <PageChromeCtx value={pageChrome}>
              <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:rounded-md focus:bg-background focus:p-4 focus:text-foreground focus:shadow-lg focus:animate-ca-enter-down-4 motion-reduce:focus:animate-none"
              >
                Skip to content
              </a>
              <AppLayoutHeader onBreadcrumbTail={setBreadcrumbTail} onPageActions={setPageActions} />
              <div
                id="main-content"
                role="region"
                aria-label="Page content"
                tabIndex={-1}
                data-full-bleed={fullBleed ? '' : undefined}
                className={cn(
                  'flex-1',
                  fullBleed ? 'flex min-h-0 flex-col overflow-hidden' : 'overflow-auto px-8 py-8 lg:px-10 lg:py-10',
                )}
              >
                <PageOutlet />
              </div>
            </PageChromeCtx>
          </SidebarInset>
          <FolioPanelSlot />
        </div>
      </AgentConnectionProvider>
    </ReleaseNotesOpenerCtx>
  );
}

function FolioLayoutProviders({ children }: { readonly children: ReactNode }) {
  return (
    <FolioToggleProvider>
      <TooltipProvider delayDuration={200}>
        <FolioSessionProvider>{children}</FolioSessionProvider>
      </TooltipProvider>
    </FolioToggleProvider>
  );
}

export function FolioLayoutRoute() {
  return (
    <FolioLayoutProviders>
      <Outlet />
    </FolioLayoutProviders>
  );
}

export function AppLayout() {
  // A workspace gets the sidebar's width too, from its first frame.
  const sidebar = useFoldingSidebar(isWorkspaceLocation(useLocation()));
  return (
    <SidebarProvider open={sidebar.open} onOpenChange={sidebar.onOpenChange}>
      <AppBreadcrumbProvider>
        <CommandPaletteRegistryProvider>
          <AppLayoutShell />
        </CommandPaletteRegistryProvider>
      </AppBreadcrumbProvider>
    </SidebarProvider>
  );
}
